import { AssetLoaderService, MfeManifest } from "../../../services/asset-loader-service";
import { ITwinEmbedCredentials, TwinService } from "../../../services/twin-service";
import confJson from "../../../shared/json/conf.json";
import { KuikaAppManager } from "../../../shared/utilty/kuika-app-manager";

export type TwinLang = "tr" | "en";

// Thrown when Twin isn't configured / the embed token can't be obtained, so the
// caller can fail closed (no element mounted) without treating it as a crash.
export class TwinMfeError extends Error {}

// Twin language follows the running app's active language (localStorage "ml",
// e.g. "tr_TR" / "en_US"). Match the app's own convention exactly: it treats only
// Turkish ("ml" === "tr_TR") as Turkish and everything else — including any other
// locale and an empty/unset "ml" (e.g. read before login finishes setting it) — as
// English (see bootstrapper.tsx / main-functions.ts). Defaulting to "tr" here was the
// bug: a non-"en…" or not-yet-set "ml" rendered Twin in Turkish while the app was English.
export function resolveTwinLang(): TwinLang {
  const ml: string = (localStorage.getItem("ml") ?? "").toLowerCase();
  return ml.startsWith("tr") ? "tr" : "en";
}

// The configured Twin deployment URL (origin). One value drives both: the MFE
// bundle is loaded from `${twinUrl}/mfe/<name>`, and the board's API root (the
// twin-url attribute) is `${twinUrl}`.
export function getConfiguredTwinUrl(): string {
  return (confJson.twinUrl ?? "").trim();
}

// Whether the Twin chat should be shown app-wide (the global FAB widget). When
// false, chat can still be added per-screen via the Twin Chat design element.
export function isTwinChatEnabled(): boolean {
  return confJson.twinChatEnabled === true;
}

export interface TwinMountOptions {
  // Chat only: "fab" (default — a floating launcher) or "inline" (full surface).
  mode?: string;
}

// Loads a Twin MFE bundle (`${twinUrl}/mfe/<mfeName>`, ES module), fetches the
// embed token at runtime, attaches a shadow root to `host`, loads the MFE's
// stylesheets INTO that shadow root (isolated from the host document), and mounts
// the custom element. Returns the mounted element. Throws TwinMfeError when Twin
// isn't configured or the embed token can't be obtained — every Twin MFE
// (dashboard, chat, discovery) authenticates with the embed token.
//
// The element only ever receives `twin-url` (the API root), the runtime-fetched
// `embed-token`, and `lang` — no agent/mcp-host URL, no dashboard-id (Twin drives
// its own board switcher), no identity secret (identify-jwt is optional and
// server-minted; omitted → anonymous shared identity).
export async function loadAndMountTwinMfe(
  mfeName: string,
  defaultTag: string,
  host: HTMLElement,
  stretch: boolean,
  options?: TwinMountOptions
): Promise<HTMLElement> {
  const twinUrl: string = getConfiguredTwinUrl();
  if (!twinUrl) {
    throw new TwinMfeError("Twin URL is not configured for this project.");
  }

  const credentials: ITwinEmbedCredentials = await TwinService.GetTwinEmbedCredentials(KuikaAppManager.GetBackendUrl());
  if (!credentials.embedToken) {
    throw new TwinMfeError("Twin embed token is not configured for this project.");
  }

  const bundleUrl: string = `${AssetLoaderService.normalizeFrontEndUrl(twinUrl)}mfe/${mfeName}`;

  // ES module (Vite build, uses import.meta). loadCss=false: stylesheets go into
  // the shadow root below so they stay isolated from the host document.
  const manifest: MfeManifest = await AssetLoaderService.loadMfeApp(bundleUrl, true, false);

  let tagName: string = defaultTag;
  if (manifest?.webComponentTag && manifest.webComponentTag.length > 0) {
    tagName = manifest.webComponentTag;
  }
  // Security: reject a tag that isn't a valid custom-element name before it
  // reaches the DOM (mirrors the MfeContainer guard).
  if (!/^[a-z][a-z0-9-]*-[a-z0-9-]*$/i.test(tagName)) {
    throw new TwinMfeError("Twin element has an invalid tag name.");
  }

  const shadowRoot: ShadowRoot = host.shadowRoot ?? host.attachShadow({ mode: "open" });

  const cssUrls: string[] = AssetLoaderService.getMfeStylesheetUrls(bundleUrl, manifest);
  await Promise.all(
    cssUrls.map((url) => AssetLoaderService.loadStylesheetIntoRoot(url, shadowRoot).catch(() => undefined))
  );

  const element: HTMLElement = document.createElement(tagName);
  element.className = "kuika_twin_mfe_live";
  element.style.display = "block";
  // stretch: inline surfaces (dashboard) fill their host; the chat widget is a
  // self-positioning FAB and must NOT be stretched.
  if (stretch) {
    element.style.width = "100%";
    element.style.height = "100%";
  }
  element.setAttribute("twin-url", twinUrl);
  element.setAttribute("embed-token", credentials.embedToken);
  // Optional per-user identity. Server-minted, short-lived; omitted → Twin uses the
  // embed token's anonymous shared identity. The signing secret never reaches here.
  if (credentials.identifyJwt) {
    element.setAttribute("identify-jwt", credentials.identifyJwt);
  }
  element.setAttribute("lang", resolveTwinLang());
  if (options?.mode) {
    element.setAttribute("mode", options.mode);
  }

  shadowRoot.appendChild(element);
  return element;
}

// Mounts the Twin chat in FAB mode (`<kuika-twin-chat mode="fab">`) into a host
// appended to <body>, NOT into the screen/canvas tree. The element renders its own
// fixed bottom-right launcher + panel; mounting at the body level keeps that
// `position:fixed` anchored to the viewport even if the app wraps screens in a
// transformed box (a transform/filter/perspective ancestor would otherwise become
// the fixed containing block). Returns the body host element so the caller can
// remove the whole widget (host → shadow root → element) on cleanup.
export async function mountTwinChatToBody(): Promise<HTMLElement> {
  const host: HTMLDivElement = document.createElement("div");
  host.className = "kuika_twin_chat_host";
  document.body.appendChild(host);
  try {
    await loadAndMountTwinMfe("chat", "kuika-twin-chat", host, /* stretch */ false, { mode: "fab" });
    return host;
  } catch (error) {
    if (host.parentNode) {
      host.parentNode.removeChild(host);
    }
    throw error;
  }
}
